/**
 * WheyDeals — auto-select an available option value.
 *
 * Problem this solves:
 *   Horizon re-renders the variant picker after every option change, marking
 *   each value available or not for the current combination. If a shopper
 *   changed flavour and the size they had selected isn't stocked in that
 *   flavour, the stale size stayed selected and the buy button read
 *   "Unavailable" — a dead end the shopper had to work out for themselves.
 *
 * Behaviour:
 *   After the picker re-renders, any option group whose selected value is no
 *   longer available is moved to its first available value. The group the
 *   shopper just clicked is never touched, so their own choice always wins.
 *
 * Deliberately standalone (rather than a patch to assets/variant-picker.js)
 * so a Horizon theme update can't silently revert it.
 */
(() => {
  const MAX_CORRECTIONS = 3;
  const SETTLE_MS = 60;

  const lastTouched = new WeakMap();
  const depth = new WeakMap();
  const timers = new WeakMap();

  function correct(picker) {
    if ((depth.get(picker) ?? 0) >= MAX_CORRECTIONS) return;

    const skip = lastTouched.get(picker);

    for (const fieldset of picker.querySelectorAll('fieldset[data-fieldset-index]')) {
      const index = Number.parseInt(fieldset.getAttribute('data-fieldset-index') ?? '', 10);
      if (!Number.isNaN(index) && index === skip) continue;

      const inputs = Array.from(fieldset.querySelectorAll('input[type="radio"]'));

      const checked =
        inputs.find((input) => input.checked) ??
        inputs.find((input) => input.dataset.currentChecked === 'true');

      // Only intervene when the current choice has become unavailable.
      if (!checked || checked.dataset.optionAvailable !== 'false') continue;

      const replacement = inputs.find(
        (input) => input !== checked && input.dataset.optionAvailable === 'true'
      );

      // Every value unavailable — genuinely sold out, leave it alone.
      if (!replacement) continue;

      depth.set(picker, (depth.get(picker) ?? 0) + 1);
      replacement.checked = true;
      replacement.dispatchEvent(new Event('change', { bubbles: true }));
      return;
    }
  }

  function schedule(picker) {
    clearTimeout(timers.get(picker));
    timers.set(picker, setTimeout(() => correct(picker), SETTLE_MS));
  }

  // Record which group the shopper touched, and reset the guard on real input.
  document.addEventListener(
    'change',
    (event) => {
      const target = event.target;
      if (!(target instanceof HTMLElement)) return;

      const picker = target.closest('variant-picker');
      if (!picker) return;

      if (event.isTrusted) depth.set(picker, 0);

      const fieldset = target.closest('fieldset[data-fieldset-index]');
      const index = fieldset
        ? Number.parseInt(fieldset.getAttribute('data-fieldset-index') ?? '', 10)
        : Number.NaN;

      if (Number.isNaN(index)) lastTouched.delete(picker);
      else lastTouched.set(picker, index);
    },
    true
  );

  // The picker is re-rendered via morph(), so watch for the DOM settling
  // rather than hooking the component's internals.
  const observed = new WeakSet();

  function watch(picker) {
    if (observed.has(picker)) return;
    observed.add(picker);
    new MutationObserver(() => schedule(picker)).observe(picker, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['data-option-available', 'checked', 'data-current-checked'],
    });
  }

  function scan() {
    document.querySelectorAll('variant-picker').forEach(watch);
  }

  scan();
  new MutationObserver(scan).observe(document.documentElement, { childList: true, subtree: true });
})();